// =====================================================================
// src/screens/v2/V2App.jsx
//
// V2 Internal Router — FINAL VERSION (all 11 screens)
//
// App.jsx hands off to V2App whenever URL contains ?v2=<something>.
//
// Supported URL params:
//   ?v2=biz-directory                — Local Businesses listing
//   ?v2=biz-profile&id=b1            — Profile of business b1
//   ?v2=connect-list                 — User's Connect List
//   ?v2=cart&id=b1                   — Cart for business b1
//   ?v2=order-detail&id=AGN-...      — Order detail
//   ?v2=order-chat&id=AGN-...        — Order chat
//   ?v2=help                         — Help & FAQ
//   ?v2=profile-v2                   — User Profile Tab (V2)
//   ?v2=members-v2                   — Members Tab (V2)
//   ?v2=onboarding-v2                — Onboarding / Welcome (V2)
//   ?v2=register-business            — Register Business 3-step wizard
//   ?v2=merchant                     — Merchant App (business owner side)
// =====================================================================

import React, { useState } from 'react';
import BusinessDirectory from './BusinessDirectory';
import BusinessProfile from './BusinessProfile';
// Phase 2.5 / C10c — single-post detail view (deep-linkable)
import BusinessWallPostDetail from './BusinessWallPostDetail';
import ConnectList from './ConnectList';
import Cart from './Cart';
import OrderDetail from './OrderDetail';
import OrderChat from './OrderChat';
import HelpFAQ from './HelpFAQ';
import ProfileTabV2 from './ProfileTabV2';
import MembersTabV2 from './MembersTabV2';
import OnboardingV2 from './OnboardingV2';
import RegisterBusiness from './RegisterBusiness';
import MerchantApp from './MerchantApp';
import MyOrders from './MyOrders';

export default function V2App({
  initialScreen,
  initialBusinessId,
  // Phase 2.5 / C10c — passed from App.jsx URL parser when the user
  // arrives via /biz/CODE/post/POSTID or ?v2=wall-post&id=...&postId=...
  initialPostId,
  onExitV2,
  currentUser,
  activeGroupId,
  // Y4: Y1's parent (App.jsx) passes V1-side tap handlers so the
  // ?v2=members-v2 deep route can route taps to V1's MemberDetail
  // and bottom-nav Profile tab. Same handlers V1's Members component
  // receives — they clear the V2 URL and set V1 screen state.
  onTapMember,
  onTapSelf,
  // My Orders: order list + business cache plumbed from App.jsx's
  // single /orders subscription. MyOrders consumes; V2App is just
  // a pass-through here.
  businessConnects = [],
  businessConnectsLoaded = false,
  businessCache = {},
}) {
  const [screen, setScreen] = useState(() => {
    const valid = [
      'biz-directory', 'biz-profile', 'connect-list', 'cart',
      'order-detail', 'order-chat', 'help', 'profile-v2',
      'members-v2', 'onboarding-v2', 'register-business', 'merchant',
      'my-orders',
      'wall-post', // Phase 2.5 / C10c — single wall post detail
    ];
    return valid.includes(initialScreen) ? initialScreen : 'biz-directory';
  });

  const [businessId, setBusinessId] = useState(initialBusinessId || null);
  const [orderId, setOrderId] = useState(initialBusinessId || null);
  // Phase 2.5 / C10c — postId for the wall-post screen. Initialized from
  // App.jsx's URL parser via the `initialPostId` prop on V2App.
  const [postId, setPostId] = useState(initialPostId || null);

  // Y8 (Jun 16): return navigation stack. Each entry captures the screen
  // + ids at the moment we navigated forward, so Back can pop and restore
  // exactly where the user came from. Empty stack → Back exits V2 (or
  // routes to a screen-specific fallback).
  //
  // Callers opt in to stack tracking via pushBack: true on navigate().
  // Forward navigations without pushBack do not save back-history —
  // useful for paths where Back has a fixed destination (e.g. Cart →
  // biz-profile is always the same business, no need to track).
  const [returnStack, setReturnStack] = useState([]);

  // Phase 2.1: directory filter/search state lifted up from BusinessDirectory.
  // Lives at the V2App level so it survives in-V2 navigation (e.g. user
  // filters Noida, opens a business profile, taps Back — the filter is
  // still applied). On full V2 exit (?v2= cleared) V2App unmounts, so
  // these reset on the next entry — intentional, prevents stale filters
  // from cross-session re-use. localStorage persistence is a future
  // upgrade if we want sticky filters across sessions.
  const [directoryCategory, setDirectoryCategory] = useState('all');
  const [directorySearch, setDirectorySearch] = useState('');
  const [directoryCity, setDirectoryCity] = useState(''); // '' = my city

  // Y7 fix: which screens use which URL id param. Used by syncUrl below
  // to keep window.location.search aligned with internal state.
  // - biz-profile / cart: 'id' param is the business ID (slug)
  // - order-detail / order-chat: 'id' param is the order's displayNumber
  // - wall-post: 'id' is the business ID AND 'postId' is the post ID
  // - all others: no 'id' param
  const SCREENS_WITH_BIZ_ID = ['biz-profile', 'cart', 'wall-post'];
  const SCREENS_WITH_ORDER_ID = ['order-detail', 'order-chat'];
  const SCREENS_WITH_POST_ID = ['wall-post'];

  // Y7 fix: keep URL in sync with internal V2 navigation. Without this,
  // V2App.navigate() updates React state but leaves window.location.search
  // stale, so:
  //   - browser refresh re-mounts on the stale URL and bounces the user
  //     back to whichever screen the URL still names
  //   - shared URLs don't reflect what the user was actually viewing
  //   - the address bar lies about the current screen
  // Using replaceState (not pushState) keeps history entries minimal:
  // browser back/forward exits V2 entirely (matching today's behaviour);
  // refresh + share now work correctly.
  function syncUrl(newScreen, newBizId, newOrderId, newPostId) {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams();
    params.set('v2', newScreen);
    if (SCREENS_WITH_BIZ_ID.includes(newScreen) && newBizId) {
      params.set('id', newBizId);
    } else if (SCREENS_WITH_ORDER_ID.includes(newScreen) && newOrderId) {
      params.set('id', newOrderId);
    }
    if (SCREENS_WITH_POST_ID.includes(newScreen) && newPostId) {
      params.set('postId', newPostId);
    }
    const nextSearch = '?' + params.toString();
    if (nextSearch !== window.location.search) {
      window.history.replaceState(null, '', nextSearch);
    }
  }

  function navigate(newScreen, params = {}) {
    // Compute the values that will be active after this navigate (state
    // updates are async, so we can't read businessId/orderId post-set).
    const nextBizId = params.businessId !== undefined ? params.businessId : businessId;
    const nextOrderId = params.orderId !== undefined ? params.orderId : orderId;
    const nextPostId = params.postId !== undefined ? params.postId : postId;
    if (params.businessId !== undefined) setBusinessId(params.businessId);
    if (params.orderId !== undefined) setOrderId(params.orderId);
    if (params.postId !== undefined) setPostId(params.postId);
    // Y8: pushBack records the screen we're leaving so Back can return to it
    if (params.pushBack) {
      setReturnStack([...returnStack, { screen, businessId, orderId, postId }]);
    }
    setScreen(newScreen);
    syncUrl(newScreen, nextBizId, nextOrderId, nextPostId);
    if (typeof window !== 'undefined') window.scrollTo(0, 0);
  }

  // Y8: pop the return stack. If empty, fall back to the screen passed in
  // (or exit V2 entirely if no fallback). The screen-and-ids tuple stored
  // in each stack entry restores the previous screen state cleanly.
  function navigateBack(fallback) {
    if (returnStack.length > 0) {
      const popped = returnStack[returnStack.length - 1];
      setReturnStack(returnStack.slice(0, -1));
      setBusinessId(popped.businessId);
      setOrderId(popped.orderId);
      setPostId(popped.postId || null);
      setScreen(popped.screen);
      syncUrl(popped.screen, popped.businessId, popped.orderId, popped.postId);
      if (typeof window !== 'undefined') window.scrollTo(0, 0);
    } else if (fallback) {
      setScreen(fallback);
      syncUrl(fallback, businessId, orderId, postId);
      if (typeof window !== 'undefined') window.scrollTo(0, 0);
    } else {
      handleExitV2();
    }
  }

  function handleExitV2() {
    if (onExitV2) onExitV2();
  }

  // ============ ROUTES ============

  if (screen === 'biz-directory') {
    return (
      <BusinessDirectory
        currentUser={currentUser}
        // Y8: stack-aware Back. Falls through to exit V2 if user landed
        // here directly (URL or freshly entered).
        onBack={() => navigateBack(null)}
        // Y8: pushBack=true so biz-profile's Back can return to Directory
        // with filters intact (Phase 2.1 state lives on V2App regardless).
        onOpenBusiness={(id) => navigate('biz-profile', { businessId: id, pushBack: true })}
        onOpenChat={() => navigate('connect-list')}
        // Phase 2.1: filter/search state lifted to V2App. BusinessDirectory
        // is now a controlled component for these three fields.
        selectedCategory={directoryCategory}
        onSelectCategory={setDirectoryCategory}
        searchQuery={directorySearch}
        onSearchChange={setDirectorySearch}
        cityFilter={directoryCity}
        onCityFilterChange={setDirectoryCity}
      />
    );
  }

  if (screen === 'biz-profile') {
    return (
      <BusinessProfile
        businessId={businessId}
        // Y8: stack-aware Back. Falls back to biz-directory for direct
        // ?v2=biz-profile URL entries (preserves existing behaviour).
        onBack={() => navigateBack('biz-directory')}
        onExitV2={handleExitV2}
        onOpenCart={(id) => navigate('cart', { businessId: id })}
        onManageBusiness={(id) => navigate('merchant', { businessId: id })}
        // Phase 2.5 / C10c — tap a wall post → detail screen.
        // pushBack=true so the detail screen's Back returns here.
        onOpenPost={(bizId, pId) => navigate('wall-post', { businessId: bizId, postId: pId, pushBack: true })}
      />
    );
  }

  if (screen === 'wall-post') {
    // Phase 2.5 / C10c — single wall post deep-linkable detail.
    // Falls back to biz-profile if Back has no stack (direct URL entry).
    return (
      <BusinessWallPostDetail
        bizCode={businessId}
        postId={postId}
        currentUser={currentUser}
        onBack={() => navigateBack('biz-profile')}
        onOpenBiz={(id) => navigate('biz-profile', { businessId: id })}
        onSetToast={() => { /* detail screen uses its own toast */ }}
      />
    );
  }

  if (screen === 'connect-list') {
    return (
      <ConnectList
        // Y8: Back exits V2 → V1 Profile. Connect List is a "yours" surface,
        // not a sibling of Directory. Falls back to V2 exit when stack empty.
        onBack={() => navigateBack(null)}
        // Y8: pushBack=true so Directory's Back returns to Connect List.
        onBrowseBusinesses={() => navigate('biz-directory', { pushBack: true })}
        // Y8: pushBack=true so biz-profile's Back returns to Connect List.
        onOpenBusiness={(id) => navigate('biz-profile', { businessId: id, pushBack: true })}
        // I4: Place order routes to cart (was a stub toast in ConnectList).
        // pushBack=true so cart Back returns to Connect List, not biz-profile.
        onOpenCart={(id) => navigate('cart', { businessId: id, pushBack: true })}
        // Path Z: pushBack so chat Back returns to ConnectList. The
        // ConnectList chat icon onOpenChat is per-card (open this
        // business's order chat); the chat-list icon onOpenChatList
        // above exits V2 to V1 ChatsList.
        onOpenChat={(orderId) => navigate('order-chat', { orderId, pushBack: true })}
        // Path Z: chat icon (top-right) opens V1 ChatsList on the
        // BusinessConnect tab. Setting sessionStorage flags before
        // clearing the URL means V1's mount-time effect will open
        // ChatsList → BusinessConnect, instead of dropping the user
        // on whatever V1 screen they last had.
        onOpenChatList={() => {
          try {
            sessionStorage.setItem('v1_returnScreen', 'chats');
            sessionStorage.setItem('v1_chatTab', 'business');
          } catch (err) { /* storage disabled */ }
          handleExitV2();
        }}
      />
    );
  }

  if (screen === 'cart') {
    return (
      <Cart
        businessId={businessId}
        // I4: cart can now be entered from Connect List (Place order)
        // as well as biz-profile (Order button). navigateBack pops the
        // stack so Back returns to whichever screen pushed cart on.
        // Fallback to biz-profile preserves behaviour for direct
        // ?v2=cart&id=X URL entries.
        onBack={() => navigateBack('biz-profile')}
        // Path Z: pushBack so chat / order-detail Back returns to Cart.
        onOpenChat={(orderId) => navigate('order-chat', { orderId, pushBack: true })}
        onOpenOrderDetail={(oid) => navigate('order-detail', { orderId: oid, pushBack: true })}
        // Path Z polish: empty cart's 'Add items' button routes to
        // biz-profile (where products live). pushBack so biz-profile's
        // Back returns to Cart after the customer adds items —
        // completing the loop.
        onAddItems={() => navigate('biz-profile', { businessId, pushBack: true })}
      />
    );
  }

  if (screen === 'order-detail') {
    return (
      <OrderDetail
        orderId={orderId}
        // Path Z: stack-aware Back. Falls back to connect-list for
        // direct ?v2=order-detail URL entries (preserves prior behaviour).
        onBack={() => navigateBack('connect-list')}
        // Path Z: pushBack so chat Back returns here, not to a hardcoded
        // screen — closes the loop for OrderDetail → Chat → Back.
        onOpenChat={(orderId) => navigate('order-chat', { orderId, pushBack: true })}
        onOpenBusiness={(bizId) => navigate('biz-profile', { businessId: bizId, pushBack: true })}
      />
    );
  }

  if (screen === 'order-chat') {
    return (
      <OrderChat
        orderId={orderId}
        businessId={businessId}
        // Path Z: stack-aware Back. Empty stack (e.g. landed here via
        // V1 BusinessConnect tap which exits V1 fresh into V2) falls
        // through to handleExitV2, returning to V1 ChatsList →
        // BusinessConnect tab via the sessionStorage round-trip flags
        // set in onOpenBusinessChat. Stacked entries (e.g.
        // OrderDetail → Chat) pop normally.
        onBack={() => navigateBack(null)}
        // Path Z: pushBack so OrderDetail Back returns here (was the
        // user-reported bug: BusinessConnect → OrderChat → View →
        // OrderDetail → Back was landing on ConnectList).
        onOpenOrder={(oid) => navigate('order-detail', { orderId: oid, pushBack: true })}
        // Path Z Commit 2: merchant View → routes to MerchantApp,
        // which auto-opens its richer OrderDetailOverlay (cancel,
        // mark-complete, status transitions). pushBack so Back from
        // MerchantApp returns to the chat.
        onOpenMerchantOrder={(oid) => navigate('merchant', { orderId: oid, pushBack: true })}
        onOpenProfile={(bizId) => navigate('biz-profile', { businessId: bizId, pushBack: true })}
      />
    );
  }

  if (screen === 'help') {
    return <HelpFAQ onBack={() => navigate('profile-v2')} />;
  }

  if (screen === 'profile-v2') {
    return (
      <ProfileTabV2
        currentUser={currentUser}
        onBack={handleExitV2}
        onOpenConnectList={() => navigate('connect-list')}
        onOpenHelp={() => navigate('help')}
        onOpenBusinessReg={() => navigate('register-business')}
        // Stub-cleanup: My Orders is now a real screen, not a toast.
        // pushBack so back-from-MyOrders returns to the Profile tab.
        onOpenMyOrders={() => navigate('my-orders', { pushBack: true })}
      />
    );
  }

  if (screen === 'members-v2') {
    return (
      <MembersTabV2
        groupId={activeGroupId}
        currentUser={currentUser}
        onBack={handleExitV2}
        onOpenBusiness={(id) => navigate('biz-profile', { businessId: id })}
        onTapMember={onTapMember}
        onTapSelf={onTapSelf}
      />
    );
  }

  if (screen === 'onboarding-v2') {
    return (
      <OnboardingV2
        onBack={handleExitV2}
        onOpenBatch={() => alert('Batch search flow — handled in V1')}
        onOpenDirectory={() => navigate('biz-directory')}
      />
    );
  }

  if (screen === 'register-business') {
    return (
      <RegisterBusiness
        currentUser={currentUser}
        onBack={handleExitV2}
        onSuccess={(newBizId) => {
          // After successful registration, drop the user straight into
          // MerchantApp so they can start setting up their products
          // and hours. MerchantApp's auto-init picks up the new doc via
          // its onAuthStateChanged → ownedBusinesses subscription. The
          // newly-created business will be activeBusinessId by default
          // since (per Step 6.0d init effect) it falls back to
          // ownedBusinesses[0] when nothing is active yet.
          navigate('merchant', { businessId: newBizId });
        }}
      />
    );
  }

  if (screen === 'merchant') {
    return (
      <MerchantApp
        currentUser={currentUser}
        onBack={handleExitV2}
        // Path Z: pushBack so OrderChat Back returns to merchant screen
        // (which already manages its own subscreen state — Today / Orders /
        // ChatList / etc — so returning here is a no-op that reads as
        // 'back where I was').
        onOpenChat={(orderId) => navigate('order-chat', { orderId, pushBack: true })}
        // Path Z Commit 2: when arriving here from order-chat's
        // View → button, orderId is in V2App state. MerchantApp uses
        // it to auto-open OrderDetailOverlay on mount. Null/undefined
        // for normal MerchantApp entries.
        initialOrderId={orderId}
      />
    );
  }

  if (screen === 'my-orders') {
    return (
      <MyOrders
        // Back: falls through to profile-v2 (the most likely entry
        // point — Profile → My Orders). Stack-aware so an entry from
        // elsewhere (deep link, future surface) still pops to where
        // the user came from.
        onBack={() => navigateBack('profile-v2')}
        // Tap a row → OrderDetail. pushBack so OrderDetail Back returns
        // to MyOrders instead of jumping to ConnectList.
        onOpenOrderDetail={(oid) => navigate('order-detail', { orderId: oid, pushBack: true })}
        // Data plumbed from App.jsx's customer-orders subscription —
        // no parallel subscription in this screen.
        businessConnects={businessConnects}
        businessConnectsLoaded={businessConnectsLoaded}
        businessCache={businessCache}
      />
    );
  }

  // Fallback
  return (
    <div style={{ padding: 40, fontFamily: 'sans-serif' }}>
      <h2>V2 screen "{screen}" not found</h2>
      <button onClick={handleExitV2}>Back to V1</button>
    </div>
  );
}
